/**
 * Membri e inviti dei set (ADR 0032), serviti da `agents`.
 *
 * Un set ha un proprietario e può avere membri con un ruolo per set: `viewer`
 * (lettore) legge, `editor` scrive al relay e gestisce i task. Il proprietario invita
 * per nome utente un utente già entrato in institor; l'invitato accetta o rifiuta;
 * l'invito scade dopo sette giorni.
 *
 * Provvisorio dichiarato, come gli altri client: percorsi scritti a mano invece che
 * generati dagli schemi OpenAPI. `team.test.ts` presidia metodo, percorso e corpo.
 */

import type { ApiClient } from './client';

/** Ruolo di un membro. */
export type MemberRole = 'viewer' | 'editor';

/** Ruoli dei membri, nell'ordine delle scelte. */
export const MEMBER_ROLES: readonly MemberRole[] = ['viewer', 'editor'];

/** Ruolo con cui il chiamante vede un set. */
export type AccessRole = 'owner' | 'admin' | MemberRole;

/** Un membro del set. */
export interface SetMember {
  /** Utente. */
  subject_id: string;
  /** Nome utente. */
  username: string;
  /** Ruolo. */
  role: MemberRole;
  /** Ingresso nel set, ISO 8601. */
  joined_at: string;
}

/** Un invito. */
export interface SetInvitation {
  /** Invito. */
  id: string;
  /** Set. */
  set_id: string;
  /** Nome del set. */
  set_name: string;
  /** Nome utente dell'invitato. */
  invitee_username: string;
  /** Ruolo offerto. */
  role: MemberRole;
  /** Nome utente di chi invita. */
  invited_by_name: string;
  /** Invio, ISO 8601. */
  created_at: string;
  /** Scadenza, ISO 8601. */
  expires_at: string;
  /** `pending` o `expired`. */
  status: 'pending' | 'expired';
}

/** Le persone di un set (`GET /api/v1/agent-sets/{id}/members`). */
export interface SetTeam {
  /** Set. */
  set_id: string;
  /** Proprietario; `username` vuoto se il set non lo conosce. */
  owner: { subject_id: string; username: string };
  /** Ruolo del chiamante. */
  my_role: AccessRole;
  /** Membri, per nome. */
  members: SetMember[];
  /** Inviti in attesa; `null` per chi non amministra il set. */
  invitations: SetInvitation[] | null;
  /** Persone ammesse dal piano (membri più inviti); `null` se senza limite o non note. */
  max_members: number | null;
  /** Membri più inviti in attesa. */
  people: number;
}

/**
 * True se il ruolo amministra il set (proprietario o `ADMIN`).
 *
 * @param role Ruolo del chiamante; `undefined` o `null` se non noto.
 * @returns L'esito; un ruolo non noto vale come amministratore, così un backend di
 *   prima (senza `my_role`) non spegne i comandi.
 */
export function administers(role: AccessRole | null | undefined): boolean {
  return role === undefined || role === null || role === 'owner' || role === 'admin';
}

/**
 * True se il ruolo scrive nel set (editor, proprietario, `ADMIN`).
 *
 * @param role Ruolo del chiamante.
 * @returns L'esito; un ruolo non noto vale come scrittura.
 */
export function writes(role: AccessRole | null | undefined): boolean {
  return role !== 'viewer';
}

/** Client dei membri e degli inviti. */
export class TeamApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Proprietario, membri e (per chi amministra il set) inviti.
   *
   * @param setId Set.
   * @returns Le persone del set.
   */
  getTeam(setId: string): Promise<SetTeam> {
    return this.client.request<SetTeam>('GET', `/api/v1/agent-sets/${encodeURIComponent(setId)}/members`);
  }

  /**
   * Invita un utente per nome utente.
   *
   * @param setId Set.
   * @param username Nome utente.
   * @param role Ruolo offerto.
   * @returns L'invito.
   */
  invite(setId: string, username: string, role: MemberRole): Promise<SetInvitation> {
    return this.client.request<SetInvitation>('POST', `/api/v1/agent-sets/${encodeURIComponent(setId)}/invitations`, {
      username,
      role,
    });
  }

  /**
   * Ritira un invito.
   *
   * @param setId Set.
   * @param invitationId Invito.
   */
  async revokeInvitation(setId: string, invitationId: string): Promise<void> {
    await this.client.request<void>(
      'DELETE',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/invitations/${encodeURIComponent(invitationId)}`,
    );
  }

  /**
   * Cambia il ruolo di un membro.
   *
   * @param setId Set.
   * @param subjectId Membro.
   * @param role Ruolo nuovo.
   * @returns Il membro.
   */
  changeRole(setId: string, subjectId: string, role: MemberRole): Promise<SetMember> {
    return this.client.request<SetMember>(
      'PATCH',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/members/${encodeURIComponent(subjectId)}`,
      { role },
    );
  }

  /**
   * Toglie un membro; con il proprio subject si esce dal set.
   *
   * @param setId Set.
   * @param subjectId Membro.
   */
  async removeMember(setId: string, subjectId: string): Promise<void> {
    await this.client.request<void>(
      'DELETE',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/members/${encodeURIComponent(subjectId)}`,
    );
  }

  /**
   * Inviti ricevuti in attesa.
   *
   * @returns Gli inviti, dal più recente.
   */
  async myInvitations(): Promise<SetInvitation[]> {
    const body = await this.client.request<{ items: SetInvitation[] }>('GET', '/api/v1/agent-sets/invitations');
    return body.items;
  }

  /**
   * Accetta un invito.
   *
   * @param invitationId Invito.
   * @returns Il membro.
   */
  accept(invitationId: string): Promise<SetMember> {
    return this.client.request<SetMember>(
      'POST',
      `/api/v1/agent-sets/invitations/${encodeURIComponent(invitationId)}/accept`,
    );
  }

  /**
   * Rifiuta un invito.
   *
   * @param invitationId Invito.
   */
  async decline(invitationId: string): Promise<void> {
    await this.client.request<void>(
      'POST',
      `/api/v1/agent-sets/invitations/${encodeURIComponent(invitationId)}/decline`,
    );
  }
}
